import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { bridgeClubAPI } from '../../services/bridgeClubAPI';

/**
 * Member-facing account (Phase 5), shown in the BC profile alongside tax
 * exemption. The member sees their store credit, bag credit, and open tab
 * (balance owed), views their history, and can pay toward their tab. Balances
 * come straight from the server (the AccountLedger) on every load — never a
 * cached number. We deliberately show the balance OWED, not the tab limit.
 *
 * Paying the tab adds a product-less tab-payment LINE to the store cart and
 * sends the member to checkout, where it's paid through the same Helcim flow as
 * feed — Red Bridge does NOT vault cards, so there is no standalone charge here.
 * On a paid order the backend writes the TAB_PAYMENT to the ledger.
 */

interface BagGrant { description: string; product: string; remaining: number; granted: number }
interface HistoryEntry { date: string; description: string; amount: string; note: string }
interface Account {
  store_credit: string;
  bag_grants: BagGrant[];
  tab: { balance: string; has_tab: boolean };
  history: HistoryEntry[];
}

const MemberAccountSection: React.FC = () => {
  const navigate = useNavigate();
  const [account, setAccount] = useState<Account | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [payAmount, setPayAmount] = useState('');
  const [paying, setPaying] = useState(false);
  const [payError, setPayError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setAccount(await bridgeClubAPI.getMemberAccount());
    } catch (e: any) {
      setError(e?.error || 'Could not load your account.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const addToCartAndCheckout = async () => {
    const amt = parseFloat(payAmount);
    if (isNaN(amt) || amt <= 0) { setPayError('Enter a valid amount.'); return; }
    if (account && amt > parseFloat(account.tab.balance)) {
      setPayError(`Enter an amount up to $${account.tab.balance}.`);
      return;
    }
    setPaying(true);
    setPayError(null);
    try {
      // Add a product-less tab-payment line, then check out through the store's
      // Helcim flow. The tab is only reduced once that order is paid (backend
      // writes TAB_PAYMENT on completion) — nothing changes here on its own.
      await bridgeClubAPI.addTabPaymentToCart(amt);
      navigate('/bridge-club/cart');
    } catch (e: any) {
      setPayError(e?.error || 'Could not add tab payment to your cart.');
      setPaying(false);  // stay on the page so the member can retry
    }
  };

  if (loading) {
    return <div className="form-section"><h3>My Account</h3><p>Loading…</p></div>;
  }
  if (error) {
    return <div className="form-section"><h3>My Account</h3><p className="error-message">{error}</p></div>;
  }
  if (!account) return null;

  const credit = parseFloat(account.store_credit);
  const nothing = credit <= 0 && account.bag_grants.length === 0 && !account.tab.has_tab;

  return (
    <div className="form-section" data-testid="member-account">
      <h3>My Account</h3>

      {credit > 0 && (
        <div className="account-row" style={{ display: 'flex', justifyContent: 'space-between' }}>
          <span>Store credit</span>
          <strong>${account.store_credit}</strong>
        </div>
      )}

      {account.bag_grants.length > 0 && (
        <div className="account-bags">
          <div className="section-desc">Bag credit</div>
          <ul>
            {account.bag_grants.map((g, i) => <li key={i}>{g.description}</li>)}
          </ul>
        </div>
      )}

      {account.tab.has_tab && (
        <div className="account-tab" style={{ marginTop: 8 }}>
          <div className="account-row" style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span>Balance owed on your tab</span>
            <strong>${account.tab.balance}</strong>
          </div>
          <div className="account-pay" style={{ display: 'flex', gap: 8, marginTop: 6 }}>
            <input
              type="number" step="0.01" min="0" placeholder="Amount to pay"
              value={payAmount} onChange={(e) => setPayAmount(e.target.value)}
            />
            <button onClick={addToCartAndCheckout} disabled={paying}>
              {paying ? 'Adding…' : 'Add to cart & check out'}
            </button>
          </div>
          {payError && <p className="error-message" role="alert">{payError}</p>}
        </div>
      )}

      {nothing && (
        <p className="section-desc">You have no store credit, bag credit, or tab balance right now.</p>
      )}

      {account.history.length > 0 && (
        <details className="account-history" style={{ marginTop: 10 }}>
          <summary>Account history</summary>
          <ul>
            {account.history.map((h, i) => (
              <li key={i}>
                {new Date(h.date).toLocaleDateString()} — {h.description} <strong>{h.amount}</strong>
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
};

export default MemberAccountSection;
